import React from 'react';
import { TIMELINE_TRACK_HEIGHT } from '../../constants/global';
import { MainBackgroundArea } from '../../styles/timeline/gridAreas';
import { CurrentTimeIndicator } from './CurrentTimeIndicator';
import { Button } from '../../styles/globalStyles';
import { TimelinePopup } from './TimelinePopup';
import { WeekEventsOverlay } from './WeekEventsOverlay';
import { CalendarEvent } from '../../hooks/useEvents';
import { DesktopOnlyWrapper } from '../../styles/timeline/popup.styles';
import { AvailabilityObject } from '../../types/schemas/abc';
import { fromZonedTime } from 'date-fns-tz';
// Number of days in a week
const DAYS_PER_WEEK = 7;
const TOTAL_CELLS = DAYS_PER_WEEK * 24;

const GRID_WALLPAPER = `
          repeating-linear-gradient(
            to bottom,
            transparent                               0px,
            transparent                               calc(${TIMELINE_TRACK_HEIGHT} / 2 - 0.5px),
            rgba(0, 0, 0, 0.1)                        calc(${TIMELINE_TRACK_HEIGHT} / 2),
            transparent                               calc(${TIMELINE_TRACK_HEIGHT} / 2 + 0.5px),
            transparent                               ${TIMELINE_TRACK_HEIGHT}
          )
        `

export interface CellClickData {
  week: number; 
  day: number; 
  hour: number; 
  rect: DOMRect; 
  date: Date; // Added date for convenience
}

interface MainBackgroundProps {
  timelineStart: Date;
  mode?: 'calendar' | 'availability';
  onCellClick: (data: CellClickData | null) => void;
  onClose: () => void;
  activeCell?: CellClickData | null;
  events?: CalendarEvent[] | null;
  availability?: AvailabilityObject | null;
  onEventClick?: (eventId: string) => void;
  activeTz?: string;
  homeTz?: string;
}

export const MainBackground: React.FC<MainBackgroundProps> = ({
  timelineStart,
  onCellClick,
  activeCell,
  events,
  availability,
  onEventClick,
  onClose,
  activeTz,
  homeTz,
}) => {


  // 2. Get userDetails, which contains the availability object

  // Convert (week, day, hour) → JS Date
  // Hour here is the grid row = hour in the active timezone
  function getDateFromCell(day: number, hour: number): Date {
    const d = new Date(timelineStart);
    d.setDate(d.getDate() + day);
    d.setHours(hour, 0, 0, 0);
    return d;
  }

  const rowCSS = `repeat(24, ${TIMELINE_TRACK_HEIGHT})`;

  return (
    <>
      <MainBackgroundArea
        css={{
          gridTemplateColumns: '1fr',
          columnGap: '0.5rem',
        }}
      >
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${DAYS_PER_WEEK}, 1fr)`,
              gridTemplateRows: rowCSS,
              position: 'relative',
              backgroundImage: GRID_WALLPAPER,
              backgroundRepeat: 'repeat-y',
            }}
          >
            {Array.from(
              { length: DAYS_PER_WEEK * 24 },
              (_: unknown, cellIndex: number) => {
                const dayIndex = cellIndex % DAYS_PER_WEEK;
                const hourIndex = Math.floor(cellIndex / DAYS_PER_WEEK);
                const isActive =
                  activeCell?.week === 0 &&
                  activeCell?.day === dayIndex &&
                  activeCell?.hour === hourIndex;

                return (
                  <Button
                    key={cellIndex}
                    type="button"
                    gridCell
                    active={isActive}
                    css={{
                      '&:hover': { backgroundColor: '$elementHover' },
                    }}
                    style={{
                      borderTop: hourIndex === 0 ? 'none' : '1px solid #ccc',
                      borderLeft: dayIndex === 0 ? 'none' : '1px solid #ccc',
                      borderRight: 'none',
                    }}
                    onClick={(e) => {
                      const rect = (
                        e.currentTarget as HTMLElement
                      ).getBoundingClientRect();
                      onCellClick({
                        week: 0, 
                        day: dayIndex, 
                        hour: hourIndex,
                        rect, 
                        date: getDateFromCell(dayIndex, hourIndex)
                      });
                    }}
                  />
                );
              }
            )}

            {/* 3. Pass the user's availability to the overlay */}
              <WeekEventsOverlay
                weekStart={timelineStart}
                availability={availability ?? null}
                events={events}
                onEventClick={onEventClick}
                activeTz={activeTz}
                homeTz={homeTz}
              />
            
          </div>

          <CurrentTimeIndicator
            timelineStart={timelineStart}
            daysPerWeek={DAYS_PER_WEEK}
            activeTz={activeTz}
          />
        </div>
      </MainBackgroundArea>

      {activeCell && (
        <DesktopOnlyWrapper>
        <TimelinePopup
          date={getDateFromCell(activeCell.day, activeCell.hour)}
          anchorRect={activeCell.rect}
          onClose={onClose}
        />
        </DesktopOnlyWrapper>
      )}
    </>
  ); 
};